import http from "./http-common";
import { getAuthToken } from "./authService";
import LegacyUploadService from "./LegacyUploadService";
import ChunkedUploadService from "./ChunkedUploadService";
import { getActiveUploader, UPLOADER_CHUNKED } from "./UploaderToggle";

const isChunked = () => getActiveUploader() === UPLOADER_CHUNKED;

const upload = (formData, onProgress, options) =>
  isChunked()
    ? ChunkedUploadService.upload(formData, onProgress, options)
    : LegacyUploadService.upload(formData, onProgress);

const cancel = (uploadId, reason, notes) =>
  isChunked()
    ? ChunkedUploadService.cancel(uploadId, reason, notes)
    : LegacyUploadService.cancel(uploadId, reason, notes);

// Pause/resume are chunked-only: legacy multipart has no resumable semantics.
const pause = (uploadId) =>
  isChunked() ? ChunkedUploadService.pauseInFlight(uploadId) : false;

const resume = (uploadId) =>
  isChunked() ? ChunkedUploadService.resumeInFlight(uploadId) : false;

// True when this tab's in-memory session for the package is parked on the resume gate.
const isPaused = (uploadId) =>
  isChunked() ? ChunkedUploadService.isPausedInFlight(uploadId) : false;

const getFiles = async (props) => {
  const token = await getAuthToken();
  const params = {
    page: props.page,
    size: props.pageSize || 10,
    sort: 'createdAt,DESC',
  };

  if (props.contributors) {
    params.contributors = props.contributors;
  }
  if (props.affiliate) {
    params.affiliate = props.affiliate;
  }
  if (props.region) {
    params.region = props.region;
  }
  if (props.country) {
    params.country = props.country;
  }
  if (props.packageName) {
    params.packageName = props.packageName;
  }
  if (props.fromDate) {
    params.from = props.fromDate;
  }
  if (props.toDate) {
    params.to = props.toDate;
  }

  return http.get(`/api/v1/metadata`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });
};

const verifyUploadStatus = async (uploadId) => {
  const token = await getAuthToken();
  return http.get("/api/v1/metadata/status", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      uploadId,
    },
  });
};

// Idempotent re-init for a package whose backend row already exists (resumable statuses
// only — the server returns the existing record + expectedFiles via PackageInitService's
// "Returning existing package" path). Used by ResumePreviousUploadBanner to upgrade a
// preInit IDB stub into a full record after a reload-during-init orphaned the client side.
const reinitPackage = async (requestBody) => {
  const token = await getAuthToken();
  return http.post("/api/v1/upload/packages", requestBody, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Upload-Id": requestBody?.uploadId,
    },
  });
};

const UploadService = {
  upload,
  getFiles,
  cancel,
  pause,
  resume,
  isPaused,
  verifyUploadStatus,
  reinitPackage,
};

export default UploadService;
